import { useEffect, useId, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useResource } from '../../hooks';
import { labelFor, signupSourceLabel, subscriptionStatusLabel } from '../../labels';
import { relativeTime } from '../../components/project/projectHealth';
import { ErrorNotice, StatusBadge } from '../../components/ui';
import { staffApi } from '../../staffApi';
import type { AddonInfo, AddonKey, StaffOrganizationSummary } from '../../staffApi';
import { StaffTableSkeleton } from './StaffApp';
import { Field, Input, Pager, Select, Table } from '../../components/primitives';

const pageSize = 50;

const subscriptionOptions = ['active', 'trialing', 'past_due', 'incomplete', 'canceled', 'none'] as const;


export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** AddonChips is the one place the console says what a client has against
 * what it uses. Included is filled; used without being included is amber
 * and says so in words, never by colour alone. */
export function AddonChips({
  catalogue,
  included,
  inUse,
}: {
  catalogue: AddonInfo[];
  included: AddonKey[];
  inUse: AddonKey[];
}) {
  const shown = catalogue.filter((addon) => included.includes(addon.key) || inUse.includes(addon.key));
  if (shown.length === 0) return <span className="muted">None</span>;
  return (
    <ul className="addon-chips">
      {shown.map((addon) => {
        const isIncluded = included.includes(addon.key);
        return (
          <li key={addon.key}>
            {isIncluded ? (
              <span className="addon-chip addon-chip--included">{addon.label}</span>
            ) : (
              <span className="addon-chip addon-chip--unpaid" title="In use but not included">
                {addon.label}
                <span className="addon-chip__flag">not included</span>
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

function lastSeen(org: StaffOrganizationSummary): string | null {
  const times = [org.last_activity_at, org.last_sign_in_at].filter((value): value is string => Boolean(value));
  if (times.length === 0) return null;
  return times.reduce((latest, value) => (new Date(value) > new Date(latest) ? value : latest));
}

export function StaffOrganizationsPage({ addons }: { addons: AddonInfo[] }) {
  const [params, setParams] = useSearchParams();
  const ids = useId();
  const search = params.get('search') ?? '';
  const subscription = params.get('subscription') ?? '';
  const access = params.get('access') ?? '';
  const addon = params.get('addon') ?? '';
  const page = Math.max(0, Number.parseInt(params.get('page') ?? '0', 10) || 0);

  // The search box answers after a short pause, not on every keystroke.
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);
  useEffect(() => {
    if (draft === search) return;
    const timer = window.setTimeout(() => update({ search: draft }), 250);
    return () => window.clearTimeout(timer);
  }, [draft]);

  const list = useResource(
    () => staffApi.listOrganizations({ search, subscription, access, addon, limit: pageSize, offset: page * pageSize }),
    [search, subscription, access, addon, page],
  );

  function update(changes: Record<string, string>) {
    const nextParams = new URLSearchParams(params);
    for (const [key, value] of Object.entries(changes)) {
      if (value) nextParams.set(key, value);
      else nextParams.delete(key);
    }
    if (!('page' in changes)) nextParams.delete('page');
    setParams(nextParams, { replace: true });
  }

  const filtered = Boolean(search || subscription || access || addon);
  const data = list.data;
  const firstShown = data && data.total > 0 ? page * pageSize + 1 : 0;
  const lastShown = data ? page * pageSize + data.organizations.length : 0;

  return (
    <section className="staff-page">
      <div className="overview-header">
        <div>
          <h1 className="overview-header__title">Organizations</h1>
        </div>
        {data ? (
          <p className="staff-count" aria-live="polite">
            {data.total} {filtered ? 'matching' : data.total === 1 ? 'organization' : 'organizations'}
          </p>
        ) : null}
      </div>

      <div className="staff-filters" role="search">
        <Field label="Search">
          <Input
            id={`${ids}-search`}
            type="search"
            name="search"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="Name, slug or member email…"
            spellCheck={false}
            autoComplete="off"
          />
        </Field>
        <Field label="Subscription">
          <Select id={`${ids}-subscription`} value={subscription} onChange={(event) => update({ subscription: event.target.value })}>
            <option value="">Any</option>
            {subscriptionOptions.map((status) => (
              <option key={status} value={status}>
                {subscriptionStatusLabel[status]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Access">
          <Select id={`${ids}-access`} value={access} onChange={(event) => update({ access: event.target.value })}>
            <option value="">Any</option>
            <option value="active">Can use the app</option>
            <option value="suspended">Suspended</option>
          </Select>
        </Field>
        <Field label="Add-on included">
          <Select id={`${ids}-addon`} value={addon} onChange={(event) => update({ addon: event.target.value })}>
            <option value="">Any</option>
            {addons.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {list.error ? <ErrorNotice error={list.error} /> : null}

      {!data && list.loading ? (
        <div role="status" aria-label="Loading organizations">
          <StaffTableSkeleton withHeader={false} />
        </div>
      ) : data && data.total === 0 ? (
        filtered ? (
          <p className="quiet-empty">
            No organizations match{' '}
            <button className="link-button" type="button" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
              Clear filters
            </button>
          </p>
        ) : (
          <p className="quiet-empty">No organizations</p>
        )
      ) : data ? (
        <>
          <Table
            label="Organizations"
            className="staff-table"
            wrapperClassName="staff-table-wrap"
            busy={list.loading}
          >
            <thead>
              <tr>
                <th scope="col">Organization</th>
                <th scope="col">Owner</th>
                <th scope="col">Joined</th>
                <th scope="col">Subscription</th>
                <th scope="col">Add-ons</th>
                <th scope="col" className="staff-table__num">Projects</th>
                <th scope="col" className="staff-table__num">Servers</th>
                <th scope="col">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {data.organizations.map((org) => {
                const seen = lastSeen(org);
                return (
                  <tr key={org.id} className={org.suspended_at ? 'staff-table__row--suspended' : undefined}>
                    <td>
                      <Link to={`/staff/organizations/${org.id}`} className="staff-table__name">
                        {org.name}
                      </Link>
                      <div className="staff-table__sub">
                        <code>{org.slug}</code>
                        {org.suspended_at ? <StatusBadge kind="organizationAccess" value="suspended" /> : null}
                      </div>
                    </td>
                    <td className="staff-table__nowrap">
                      {org.owner_email ? (
                        <>
                          <div>{org.owner_name}</div>
                          <div className="staff-table__sub">{org.owner_email}</div>
                        </>
                      ) : (
                        <span className="muted">No owner</span>
                      )}
                    </td>
                    <td className="staff-table__nowrap">
                      <div>{formatDate(org.created_at)}</div>
                      <div className="staff-table__sub">{labelFor(signupSourceLabel, org.signup_source)}</div>
                    </td>
                    <td className="staff-table__nowrap">
                      <StatusBadge kind="subscription" value={org.subscription_status} />
                      {org.subscription_current_period_end ? (
                        <div className="staff-table__sub">Period ends {formatDate(org.subscription_current_period_end)}</div>
                      ) : null}
                    </td>
                    <td>
                      <AddonChips catalogue={addons} included={org.addons} inUse={org.addons_in_use} />
                    </td>
                    <td className="staff-table__num">{org.project_count}</td>
                    <td className="staff-table__num">{org.server_count}</td>
                    <td className="staff-table__nowrap">
                      {seen ? (
                        <time dateTime={seen} title={new Date(seen).toLocaleString()}>
                          {relativeTime(seen)}
                        </time>
                      ) : (
                        <span className="muted">Never</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
          <Pager
            first={firstShown}
            last={lastShown}
            total={data.total}
            busy={list.loading}
            label="Organisation pages"
            onPrevious={() => update({ page: String(page - 1) })}
            onNext={() => update({ page: String(page + 1) })}
          />
        </>
      ) : null}
    </section>
  );
}
